import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import InputField from "@client/components/ui/InputField";
import Modal from "@client/components/ui/Modal";
import partnerImage from "@client/components/ui/sharedImages/partner.svg";
import licensorStore from "@client/stores/LicensorStore";
import notificationStore from "@client/stores/NotificationStore";
import partnerStore from "@client/stores/PartnerStore";
import Style from "@client/utils/Style";
import { InputField as InputFieldType } from "@typings/InputField";
import { Partner, PartnerInput } from "@typings/Partner";

import inputFieldsSchema from "./InputFields";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0;
`;
const PartnerImage = styled.div`
  ${Style.exactSize("96px")}
  background: url('${partnerImage}') center no-repeat;
  background-size: contain;
`;
const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 32px;
  justify-content: space-between;
  margin-top: -16px;
  > div {
    margin-top: 16px;
  }
`;
const PolicySection = styled.div`
  margin: 8px 32px 0 32px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;
const PolicySectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #777a8b;
  margin-bottom: 8px;
`;
const PolicyFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: -16px;
  > div {
    margin-top: 16px;
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 48px 32px 32px 32px;
`;

const POLICY_FIELD_NAMES = [
  "policy_restrict_tags",
  "policy_fixed_type_only",
  "policy_nfr_default_replicas",
  "policy_nfr_default_migrations",
  "policy_allow_sap_licences",
];

type State = {
  inputFields: InputFieldType[];
  highlightedFields: string[];
};
type Props = {
  onRequestClose: () => void;
  partner?: Partner | null;
};

@observer
class PartnerModal extends React.Component<Props, State> {
  state: State = {
    inputFields: [],
    highlightedFields: [],
  };

  UNSAFE_componentWillMount() {
    let inputFields = [...inputFieldsSchema.FIELDS];
    const partner = this.props.partner;
    if (partner) {
      // Initialize regular fields
      inputFields = inputFields.map(field => {
        if (POLICY_FIELD_NAMES.includes(field.name)) {
          return field;
        }
        const name: keyof Partner = field.name as any;
        const value = partner![name];
        if (value) {
          return { ...field, value };
        }
        return { ...field };
      });
      // Initialize policy fields from existing licensing_policy
      inputFields = inputFieldsSchema.initPolicyFields(
        inputFields,
        partner.licensing_policy ?? null,
      );
    }
    this.setState({ inputFields });
  }

  handleFieldChange(field: InputFieldType, value: any) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === field.name ? { ...f, value } : { ...f },
    );
    this.setState({ inputFields });
  }

  async handleSaveClick() {
    const messages: string[] = [];
    const highlightedFields = this.state.inputFields
      .filter(f => {
        if (
          (f.name === "policy_nfr_default_replicas" ||
            f.name === "policy_nfr_default_migrations") &&
          !this.showNfrQuantity
        ) {
          return false;
        }
        if (f.validate) {
          const result = f.validate(f, this.state.inputFields);
          if (typeof result === "string") {
            messages.push(result);
            return true;
          }
          return !result;
        }
        if (!f.required || f.value) {
          return false;
        }
        return true;
      })
      .map(f => f.name);
    this.setState({ highlightedFields });
    if (highlightedFields.length) {
      if (messages.length) {
        notificationStore.alert(messages[0], "error");
      }
      return;
    }
    const partner: PartnerInput = inputFieldsSchema.buildData(
      this.state.inputFields,
    );
    if (this.props.partner) {
      const success = await partnerStore.update(this.props.partner.id, partner);
      if (success) {
        await this.refreshOwnPartnerPolicy();
        this.props.onRequestClose();
      }
    } else {
      const addedPartner = await partnerStore.add(partner);
      if (addedPartner) {
        await this.refreshOwnPartnerPolicy();
        this.props.onRequestClose();
      }
    }
  }

  async refreshOwnPartnerPolicy() {
    try {
      await licensorStore.refreshPartnerPolicy();
    } catch (err) {
      // ignored on purpose, see above
    }
  }

  get showNfrQuantity(): boolean {
    return !!this.state.inputFields.find(f => f.name === "policy_restrict_tags")
      ?.value;
  }

  renderButtons() {
    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={partnerStore.updating}
        >
          Cancel
        </Button>
        {partnerStore.updating ? (
          <ButtonLoading large>Saving...</ButtonLoading>
        ) : (
          <Button
            large
            disabled={partnerStore.updating}
            onClick={() => {
              this.handleSaveClick();
            }}
          >
            Save
          </Button>
        )}
      </Buttons>
    );
  }

  renderFields() {
    const regularFields = this.state.inputFields.filter(
      f => !POLICY_FIELD_NAMES.includes(f.name),
    );
    const policyFields = this.state.inputFields.filter(f =>
      POLICY_FIELD_NAMES.includes(f.name),
    );

    return (
      <>
        <Fields>
          {regularFields.map(field => (
            <InputField
              key={field.name}
              field={field}
              disabled={
                partnerStore.updating ||
                (field.name === "name" &&
                  this.props.partner?.name === "Cloudbase Solutions")
              }
              highlight={
                !!this.state.highlightedFields.find(name => field.name === name)
              }
              onChange={value => {
                this.handleFieldChange(field, value);
              }}
            />
          ))}
        </Fields>
        <PolicySection>
          <PolicySectionTitle>Licensing Policy</PolicySectionTitle>
          <PolicyFields>
            {policyFields.map(field => {
              if (
                (field.name === "policy_nfr_default_replicas" ||
                  field.name === "policy_nfr_default_migrations") &&
                !this.showNfrQuantity
              ) {
                return null;
              }
              return (
                <InputField
                  key={field.name}
                  field={field}
                  disabled={partnerStore.updating}
                  highlight={
                    !!this.state.highlightedFields.find(
                      name => field.name === name,
                    )
                  }
                  onChange={value => {
                    this.handleFieldChange(field, value);
                  }}
                />
              );
            })}
          </PolicyFields>
        </PolicySection>
      </>
    );
  }

  render() {
    return (
      <Modal
        isOpen
        title={this.props.partner ? "Edit Partner" : "New Partner"}
        onRequestClose={this.props.onRequestClose}
      >
        <Wrapper>
          <Header>
            <PartnerImage />
          </Header>
          {this.renderFields()}
          {this.renderButtons()}
        </Wrapper>
      </Modal>
    );
  }
}

export default PartnerModal;
